import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Spinner, Alert } from '../components/ui';

export function redirectByRole(role, navigate) {
  if (role === 'SUPER_ADMIN') navigate('/dashboard/super-admin');
  else if (role === 'ADMIN')  navigate('/dashboard/admin');
  else                         navigate('/dashboard/employee');
}

export default function Login() {
  const { login }            = useAuth();
  const navigate             = useNavigate();
  const [employeeId, setEmp] = useState('');
  const [password,   setPwd] = useState('');
  const [showPwd,    setShow]= useState(false);
  const [error,      setErr] = useState('');
  const [loading,    setLoad]= useState(false);
  const [isMobile,   setMob] = useState(window.innerWidth < 768);
  React.useEffect(() => {
    const r = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId.trim() || !password.trim()) {
      setErr('Enter your Employee ID and password to continue');
      return;
    }
    setErr(''); setLoad(true);
    try {
      const user = await login(employeeId.trim(), password);
      if (user.mfaRequired) {
        navigate('/mfa-verify', { state: { tempToken: user.tempToken } });
        return;
      }
      if (user.mfaSetupRequired) {
        navigate('/mfa-setup');
        return;
      }
      if (user.forceReset) navigate('/reset-password');
      else redirectByRole(user.role, navigate);
    } catch (err) {
      const raw = err.response?.data?.message || err.message || 'Login failed';
      const msg = raw.includes(':') ? raw.split(':').slice(1).join(':').trim() : raw;
      setErr(msg);
    } finally {
      setLoad(false);
    }
  };

  return (
    <div style={S.page}>
      {/* Left panel — brand / features (desktop only) */}
      {!isMobile && <div style={S.leftPanel}>
        <div style={S.brandArea}>
          <div style={S.logoWrap}>
            <div style={S.logoMark}>K</div>
            <div>
              <p style={S.brandName}>Tracern</p>
              <p style={S.brandSub}>by Mahashunya Innovation Labs</p>
            </div>
          </div>
          <h2 style={S.heroText}>
            Every task has an<br/>owner. Every deadline<br/>is remembered.
          </h2>
          <div style={S.featureList}>
            {[
              { icon: '🎯', text: 'Assign tasks with clear deadlines' },
              { icon: '⚡', text: 'Auto-escalate overdue work' },
              { icon: '📊', text: 'Full audit trail, zero excuses' },
              { icon: '🔔', text: 'Daily digest keeps everyone aligned' },
            ].map((f, i) => (
              <div key={i} style={S.feature}>
                <span style={S.featureIcon}>{f.icon}</span>
                <span style={S.featureText}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>
        <p style={S.leftFooter}>© {new Date().getFullYear()} Mahashunya Innovation Labs. All rights reserved.</p>
      </div>}

      {/* Right panel — form */}
      <div style={S.rightPanel}>
        <div style={S.formWrap}>
          {/* Mobile-only logo */}
          {isMobile && <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px' }}>
            <div style={S.logoMarkSm}>K</div>
            <div>
              <p style={S.brandNameSm}>Tracern</p>
              <p style={S.brandSubSm}>by Mahashunya</p>
            </div>
          </div>}

          <h1 style={S.formTitle}>Welcome back</h1>
          <p style={S.formSubtitle}>Sign in to your workspace</p>

          {error && (
            <div style={S.errorBox}>
              <span style={{ fontSize: '15px' }}>⚠</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} style={S.form}>
            <div style={S.field}>
              <label style={S.label}>Employee ID</label>
              <input
                value={employeeId}
                onChange={e => setEmp(e.target.value)}
                placeholder="e.g. SA001"
                autoCapitalize="none"
                autoComplete="username"
                style={S.input}
                onFocus={e => e.target.style.borderColor = '#2563EB'}
                onBlur={e  => e.target.style.borderColor = '#E5E7EB'}
              />
            </div>

            <div style={S.field}>
              <label style={S.label}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPwd(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  style={{ ...S.input, paddingRight: '50px' }}
                  onFocus={e => e.target.style.borderColor = '#2563EB'}
                  onBlur={e  => e.target.style.borderColor = '#E5E7EB'}
                />
                <button
                  type="button"
                  onClick={() => setShow(s => !s)}
                  style={S.eyeBtn}
                  tabIndex={-1}
                >
                  {showPwd ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} style={S.submitBtn}>
              {loading
                ? <><Spinner size={18} color="#fff" /> Signing in…</>
                : 'Sign In →'
              }
            </button>
          </form>

          <p style={S.hint}>
            Forgot your password?{' '}
            <span style={{ color: '#2563EB', fontWeight: 500 }}>Contact your Admin</span>
          </p>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100dvh',
    display: 'flex',
    background: '#fff',
  },
  /* Left */
  leftPanel: {
    flex: '0 0 420px',
    background: 'linear-gradient(160deg, #1E3A8A 0%, #2563EB 50%, #6366F1 100%)',
    display: 'flex', flexDirection: 'column',
    padding: '48px 40px',
    color: '#fff',
  },
  brandArea: { flex: 1, display: 'flex', flexDirection: 'column', gap: '40px' },
  logoWrap:  { display: 'flex', alignItems: 'center', gap: '14px' },
  logoMark: {
    width: '48px', height: '48px', borderRadius: '14px',
    background: 'rgba(255,255,255,0.2)',
    backdropFilter: 'blur(10px)',
    border: '1.5px solid rgba(255,255,255,0.3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '24px', fontWeight: 800, color: '#fff',
    fontFamily: "'Sora', sans-serif",
    flexShrink: 0,
  },
  brandName: { fontSize: '24px', fontWeight: 800, letterSpacing: '-0.04em', fontFamily: "'Sora', sans-serif", lineHeight: 1 },
  brandSub:  { fontSize: '11px', opacity: 0.6, letterSpacing: '0.04em', marginTop: '3px' },
  heroText: {
    fontSize: '32px', fontWeight: 800, lineHeight: 1.25,
    letterSpacing: '-0.03em',
    fontFamily: "'Sora', sans-serif",
  },
  featureList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  feature:     { display: 'flex', alignItems: 'center', gap: '12px' },
  featureIcon: {
    width: '36px', height: '36px', borderRadius: '10px',
    background: 'rgba(255,255,255,0.15)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', flexShrink: 0,
  },
  featureText: { fontSize: '14px', opacity: 0.9, fontWeight: 500 },
  leftFooter:  { fontSize: '11px', opacity: 0.4, marginTop: '32px' },

  /* Right */
  rightPanel: {
    flex: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '32px 24px',
    background: '#FAFAFA',
  },
  formWrap: {
    width: '100%', maxWidth: '400px',
    background: '#fff',
    borderRadius: '24px',
    border: '1px solid #E5E7EB',
    padding: '40px 36px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
  },


  logoMarkSm: {
    width: '38px', height: '38px', borderRadius: '10px',
    background: 'linear-gradient(135deg, #2563EB 0%, #6366F1 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '18px', fontWeight: 800, color: '#fff',
    fontFamily: "'Sora', sans-serif",
  },
  brandNameSm: { fontSize: '18px', fontWeight: 800, letterSpacing: '-0.03em', fontFamily: "'Sora', sans-serif", lineHeight: 1, color: '#111827' },
  brandSubSm:  { fontSize: '10px', color: '#9CA3AF', letterSpacing: '0.04em', marginTop: '2px' },

  formTitle:    { fontSize: '26px', fontWeight: 800, color: '#111827', letterSpacing: '-0.04em', marginBottom: '6px', fontFamily: "'Sora', sans-serif" },
  formSubtitle: { fontSize: '14px', color: '#6B7280', marginBottom: '28px' },

  errorBox: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 16px', borderRadius: '12px',
    background: '#FEF2F2', color: '#DC2626',
    border: '1px solid #FECACA',
    fontSize: '14px', fontWeight: 500, marginBottom: '20px',
  },

  form:  { display: 'flex', flexDirection: 'column', gap: '18px' },
  field: { display: 'flex', flexDirection: 'column', gap: '7px' },
  label: { fontSize: '13px', fontWeight: 600, color: '#374151', letterSpacing: '-0.005em' },
  input: {
    width: '100%', padding: '12px 16px',
    borderRadius: '12px',
    border: '1.5px solid #E5E7EB',
    background: '#F9FAFB',
    fontSize: '15px', color: '#111827',
    outline: 'none',
    transition: 'border-color 0.12s',
    minHeight: '48px',
  },
  eyeBtn: {
    position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '16px', padding: '4px', color: '#9CA3AF',
  },
  submitBtn: {
    width: '100%', padding: '14px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #2563EB 0%, #6366F1 100%)',
    color: '#fff', border: 'none',
    fontSize: '15px', fontWeight: 700,
    cursor: 'pointer', marginTop: '4px',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    minHeight: '50px',
    boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
    letterSpacing: '-0.01em',
    transition: 'opacity 0.12s, transform 0.12s',
  },
  hint: { textAlign: 'center', fontSize: '13px', color: '#6B7280', marginTop: '20px' },
};
